/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import type { OrtbOutboundMapping } from '../../types';
import type { DiagnosisFixTarget } from '../../utils/test-diagnosis';
import { detectedOriginFor, detectedOriginForMatch, parseSampleUrl } from '../../utils/mapping-suggest';
import {
  createParameterRow,
  getMacroDefaultForParam,
  readValueOrigin,
  rowsToParamsDto,
  type ParameterRow,
} from '../shared/editors/parameter-editor.adapters';

/**
 * The wizard's four phases (#896 · R1), and the one place their indices are
 * written down. Anything that deep-links into the wizard — a diagnosis Fix, the
 * detail page's section Edit buttons — resolves through here rather than
 * hard-coding a number that silently means something else after a reorder.
 *
 * ## The names are the detail page's names (`ST11`, ruled by Todd 2026-09-22)
 *
 * `ST11` opened as *Identity* and *Identify* being one letter apart; Todd widened it to the whole
 * vocabulary, across both surfaces: *"we should rename both the steps in the wizard, and the
 * sections in the detail page. They are a little misleading confusing now."*
 *
 * | Was | Is | Why |
 * | --- | --- | --- |
 * | `Identify` | **`Connection`** | it is the endpoint, the protocol, its version and the timeout — which is exactly the detail page's **Connection** section, and the detail page already had to translate (`WIZARD_STEP_CONNECTION = PHASE_INDEX.Identify`) |
 * | `Connect` | **`Configure Request`** | it authors headers, parameters and the ORTB body: what is *sent*, not what is connected to. Two phases called *Identify* and *Connect* for *what am I connecting to* and *what will be sent* had the vocabulary inverted |
 * | `Prove & map` | unchanged | see below |
 * | `Name and save` | unchanged | |
 *
 * **`Prove & map` keeps its name, and that is an answer rather than a deferral.** Todd left it open
 * against a question — *does `#947` bring the test/prove concept to both steps?* — and `#947` is a
 * **Retest button on the editing phase that jumps here and runs**, so proving still happens on this
 * phase and only this one. Renaming it *Map Response* would drop the half `#992` deliberately fused
 * (`R15`: testing and mapping are one action, and a step boundary through one activity re-creates
 * the duplicate-test problem it removed).
 *
 * **The keys are operator-facing words, so a rename is a rename here too.** That is deliberate:
 * `PHASE_INDEX.Connection` is checkable against the rail by reading it, where a stable internal key
 * would let the two drift silently — which is the thing `ST11` is about.
 */
export const PHASE_INDEX = {
  Connection: 0,
  'Configure Request': 1,
  'Prove & map': 2,
  'Name and save': 3,
} as const;

/**
 * Where a diagnosis Fix stages its edit (#895 · R3) — indices into the wizard's
 * four phases (`0 = Connection, 1 = Configure Request, 2 = Prove & map, 3 = Name and save`). A
 * Fix is a staged edit, never a nav hop (guidance §7, §12): the surface that owns
 * the value opens where the operator already is.
 *
 * Re-pointed at the phases by #896. Under the old five-step flow this mapped to
 * step indices that only existed in advanced mode, so a Fix targeting a mapping
 * step had to switch modes first. Removing the split deleted that branch — every
 * target is now a phase that always exists.
 *
 * `connection` means the endpoint, the protocol, its version and the timeout —
 * all four authored on the phase now *called* **Connection** (#995 · R13, renamed by `ST11`), and
 * all four the detail page's **Connection** section. Version and timeout briefly sat with the
 * request mappings; that split a protocol from its own version and is what left version off the
 * detail page entirely.
 */
export const WIZARD_STEP_FOR_FIX_TARGET: Record<DiagnosisFixTarget, number> = {
  connection: 0,
  params: 1,
  headers: 1,
  ortbBody: 1,
  responseMapping: 2,
};

// Inbound response field mapping is authored via the Universal Mapping Model
// (#866) — the former flat ORTB field-mapping helpers were removed with the
// fixed-slot config. Outbound-mapping helpers remain below.

/**
 * Returns true if the outbound mapping has any non-empty sections.
 */
export function hasOrtbOutboundMappingOverrides(mapping: OrtbOutboundMapping): boolean {
  return (
    hasNonEmptySection(mapping.request as Record<string, unknown> | undefined) ||
    hasNonEmptySection(mapping.site as Record<string, unknown> | undefined) ||
    hasNonEmptySection(mapping.app as Record<string, unknown> | undefined) ||
    hasNonEmptySection(mapping.device as Record<string, unknown> | undefined) ||
    hasNonEmptySection(mapping.user as Record<string, unknown> | undefined) ||
    hasNonEmptySection(mapping.source as Record<string, unknown> | undefined)
  );
}

function hasNonEmptySection(section: Record<string, unknown> | undefined): boolean {
  if (!section) return false;
  return Object.values(section).some((v) => {
    if (v === undefined || v === null || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  });
}

/**
 * Strip empty/undefined values from an outbound mapping section object.
 * Returns undefined if the result has no meaningful keys.
 */
function sanitizeSection<T extends Record<string, unknown>>(section: T | undefined): T | undefined {
  if (!section) return undefined;
  const result: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(section)) {
    if (v === undefined || v === null || v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) continue;
    result[k] = v;
  }
  return Object.keys(result).length > 0 ? (result as T) : undefined;
}

/**
 * Build a payload-safe OrtbOutboundMapping.
 * Strips empty sections and empty string fields.
 * Returns undefined when nothing is configured.
 */
export function sanitizeOrtbOutboundMapping(mapping: OrtbOutboundMapping): OrtbOutboundMapping | undefined {
  const sanitized: OrtbOutboundMapping = {};

  const req = sanitizeSection(mapping.request as unknown as Record<string, unknown>);
  if (req) sanitized.request = req as OrtbOutboundMapping['request'];

  const site = sanitizeSection(mapping.site as unknown as Record<string, unknown>);
  if (site) sanitized.site = site as OrtbOutboundMapping['site'];

  const app = sanitizeSection(mapping.app as unknown as Record<string, unknown>);
  if (app) sanitized.app = app as OrtbOutboundMapping['app'];

  const device = sanitizeSection(mapping.device as unknown as Record<string, unknown>);
  if (device) sanitized.device = device as OrtbOutboundMapping['device'];

  const user = sanitizeSection(mapping.user as unknown as Record<string, unknown>);
  if (user) sanitized.user = user as OrtbOutboundMapping['user'];

  const source = sanitizeSection(mapping.source as unknown as Record<string, unknown>);
  if (source) sanitized.source = source as OrtbOutboundMapping['source'];

  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

/**
 * **Moved to `utils/demand-source-coverage.ts`** (`TM4`, `#1170`) and re-exported here so the
 * wizard's import list does not have to move with it.
 *
 * It was a copy of the same regex in `libs/adapters/ad-server-presets.ts`, written twice because the
 * catalogue it belonged to could not express the class it stood in for. `D32`'s `operator` case now
 * can, so it lives beside the declaration it derives and there is one of it.
 */
export {
  endpointHasPlaceholder as hasUnfilledEndpointPlaceholder,
  unfilledEndpointPlaceholders,
} from '../../utils/demand-source-coverage';

/**
 * Is this endpoint still just a suggestion we placed, rather than the operator's own value?
 *
 * Selecting an ad-server template seeds the endpoint *shape* (#955). Selecting a different
 * template then has to replace it — but must not touch a URL the operator typed or pasted,
 * which is the one part of the configuration they could not have got from us.
 *
 * The first cut tested "is the field empty", which cannot tell the two apart: once any
 * template had filled the field, every later template left it alone, and switching
 * template presented as *"the suggestion doesn't fill for this server"* rather than as the
 * previous suggestion refusing to leave. Both halves of that were reported from the same
 * smoke test.
 *
 * An endpoint that still exactly matches some preset's template is, by definition, one we
 * put there and nobody has touched. Matching against the whole catalog rather than
 * remembering the last selection keeps this stateless — re-selecting, going back a step,
 * or loading a draft all behave the same way.
 */
export function isUneditedTemplateEndpoint(
  endpoint: string | undefined,
  templates: ReadonlyArray<{ endpoint?: string }>,
): boolean {
  const value = (endpoint ?? '').trim();
  if (value === '') return true;
  return templates.some((t) => t.endpoint && t.endpoint === value);
}

/**
 * Split a pasted endpoint into the URL we call and the query-parameter rows it carried
 * (#874 · D18).
 *
 * *"I gave you the URL I call, why'd you change it?"* — we did not. The parameters keep going
 * out; they are now **visible and editable** instead of buried in a string, and the driver
 * appends exactly these rows back onto the endpoint. Leaving them in the endpoint *and*
 * showing them as rows would be the worse option: two homes for one value, and the row wins
 * silently because the driver `set`s it over whatever the endpoint said.
 *
 * So the query string is removed **whenever it parses**, not only when something new was
 * added — otherwise a source whose parameters are all already authored keeps a dead copy in
 * its endpoint that looks authoritative and is not.
 *
 * **A name that already has a row is not overwritten, and a differing value is reported
 * rather than dropped.** The operator's row is the authored artifact; the pasted endpoint is
 * an offer. Silently taking either one would be the decision this design refuses to make for
 * them.
 *
 * Deliberately reuses `parseSampleUrl`, the same parser the Query Parameters paste panel
 * uses, so an endpoint and a pasted sample cannot disagree about what a parameter is or
 * which macro it maps to (V6 — two intake paths, one parser).
 */
/**
 * **What a row switch keeps, withdraws and adds when the operator changes template** (`#1115`).
 *
 * Beside {@link isUneditedTemplateEndpoint} because it is the same question one field over, and that
 * adjacency is the point: the endpoint had exactly this defect, its fix is commented in place, and
 * the rows kept it anyway — *"the second preset you tried silently kept the first one's endpoint"*,
 * reported again a month later about parameters.
 *
 * The guard this replaces was `prev.length > 0 ? prev : seeded`. Its intent was right — never
 * destroy what the operator typed — and its reach was wrong: it could not tell a seeded row from an
 * authored one, so the **first** template chosen seeded rows and every later one seeded nothing.
 * Google's `iu` survived a switch to FreeWheel, where it means nothing.
 *
 * Three groups, treated differently:
 *
 * | | |
 * | --- | --- |
 * | Still matches what the **previous** template seeded | **withdrawn** — ours, untouched, and about a vendor the operator has left |
 * | Anything else the operator holds — typed, pasted, or seeded then corrected | **kept**, which is the old guard's correct intent |
 * | The new template's rows, for names nothing already holds | **added**, so choosing a template *after* pasting a tag gives the union rather than silently seeding nothing |
 *
 * **Matched on content, not on provenance.** `provenance: 'template'` survives an edit, so a row the
 * operator corrected would still look like ours and be replaced out from under them. Recomputing
 * what the previous template produced and comparing is the same test the endpoint already applies:
 * a value that still matches what a template produced is by definition one we put there.
 *
 * @param current what the form holds now
 * @param previouslySeeded what the template being left would have seeded — empty when there was none
 * @param nowSeeded what the template being chosen wants
 */
export function mergeTemplateRows<T extends { name: string; mode: string; value?: unknown; expression?: unknown }>(
  current: readonly T[],
  previouslySeeded: readonly T[],
  nowSeeded: readonly T[],
): T[] {
  return mergeTemplateItems(
    current,
    previouslySeeded,
    nowSeeded,
    (row) => row.name.trim().toLowerCase(),
    (a, b) =>
      // A row's value is its literal or its one expression (`X58`); `mappingSource` is never on a row now.
      a.mode === b.mode &&
      (a.value ?? '') === (b.value ?? '') &&
      JSON.stringify(a.expression ?? null) === JSON.stringify(b.expression ?? null),
  );
}

/**
 * **The withdraw-on-switch rule itself, with the address and the sameness left to the caller**
 * (`TM-A`, `#1169`; generalised from {@link mergeTemplateRows}, `#1115`).
 *
 * `#1115` solved this for query-parameter rows and the reasoning above is entirely about *rows* —
 * but nothing in it is: *what the template being left still owns is withdrawn, what the operator
 * holds is kept, what the new template wants is added* is a statement about **an addressable
 * collection a template contributes to**, and this wizard has three of them (query parameters,
 * headers, ORTB body leaves).
 *
 * Writing it a second time for headers and a third for the body is exactly what CLAUDE.md's
 * *sharing the widget is not sufficient — share the round trip* rule names, arriving one layer up:
 * the three would look on-paradigm and drift, and the drift would be invisible until an operator
 * switched template twice on the surface nobody wrote the test for.
 *
 * Two things vary per collection and neither is the rule:
 *
 * - **`keyOf`** — a parameter's name, a header's key, a leaf's path. What *the same slot* means.
 * - **`sameValue`** — what *unchanged since the template put it there* means, given that slot.
 *   Matched on **content, not provenance**, for the reason {@link mergeTemplateRows} records: a
 *   `provenance: 'template'` mark survives an edit, so a corrected row would still read as ours.
 */
export function mergeTemplateItems<T>(
  current: readonly T[],
  previouslySeeded: readonly T[],
  nowSeeded: readonly T[],
  keyOf: (item: T) => string,
  sameValue: (a: T, b: T) => boolean,
): T[] {
  const isSeeded = (item: T, seeded: readonly T[]): boolean =>
    seeded.some((candidate) => keyOf(candidate) === keyOf(item) && sameValue(candidate, item));

  const kept = current.filter((item) => !isSeeded(item, previouslySeeded));
  const held = new Set(kept.map(keyOf));
  return [...kept, ...nowSeeded.filter((item) => !held.has(keyOf(item)))];
}

/**
 * **The Quick-fill's rows, from what the tag parser detected** (R4b, #951 · R5, `#1001`).
 *
 * Moved out of the component in `#1001` for the reason `shouldConfirmClose` lives here: the judgement
 * is the whole of it, the component cannot be render-tested here, and the case that was wrong — a
 * literal in the macro slot — is invisible in the happy path.
 */
export function buildRowsFromDetectedParameters(
  detectedParameters: Array<{
    name: string;
    value?: string;
    isMacro?: boolean;
    type?: 'static' | 'dynamic';
    description?: string;
    mappingSource?: string;
    optional?: boolean;
    /**
     * **The parser's default — what is sent when the session does not carry this value** (`#978`).
     *
     * `classifyAdTagParameter` keeps a sample's literal as the default where the policy allows it
     * (`app_bundle=13535` on a single-app publisher, `#952`). This type did not declare it, and a
     * narrower structural type accepts a wider argument, so `tsc` was silent while the wizard dropped
     * it — the row resolved to nothing and the parameter vanished on a thin session.
     */
    defaultValue?: string | number;
    /** The backend's origin, read through the one legacy read — the parser may predate `#1001`. */
    provenance?: unknown;
    matchesAutoDefault?: boolean;
  }>,
): ParameterRow[] {
  const seen = new Set<string>();
  return detectedParameters
    .filter((param) => {
      const name = param.name?.trim();
      if (!name) return false;
      /**
       * A detection identical to what the driver sends anyway becomes **no row** (#951 ·
       * R5). It states nothing — the value goes out the same with or without it — and a
       * row beside a panel saying "sent automatically" contradicts the panel, implying the
       * operator owns something they do not.
       *
       * Only the *additional* parameters a tag carried become rows: the SSP's own names
       * for standard concepts (`player_width`, `content_duration`), a value pinned under a
       * standard name, and anything outside the auto set. Those are the overrides, and
       * overrides are what a row is for.
       */
      if (param.matchesAutoDefault) return false;
      const key = name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .map((param) => {
      const name = param.name.trim();
      const defaults = getMacroDefaultForParam(name);
      const macroByType = param.type === 'dynamic' || param.isMacro === true;
      const hasMacroDefault = !!defaults?.defaultMappingSource;
      const mode: ParameterRow['mode'] = macroByType || hasMacroDefault ? 'macro' : 'static';

      const rawValue = String(param.value ?? '').trim();
      /**
       * **A parameter the parser kept as written, and the console maps on its own name** (`#1001`).
       *
       * The parser does not know `content_language`, so it returns the tag's `en` as a static value;
       * `getMacroDefaultForParam` then finds `{language}` on one word of the name. The macro slot was
       * filled with `rawValue` first — so the row became a macro row whose macro was **`en`**, the
       * tag's literal in the slot the token belongs in. The token is the match's; the literal is what
       * Revert returns to, and the origin says the match was on part of the name.
       */
      const consoleMapped = !macroByType && hasMacroDefault;
      // `N11f` — a macro row's token goes to the macro slot, so the row's *default* stays free.
      // `createParameterRow` splits either way; naming the slot here is what stops the next
      // caller putting the token back where the literal lives.
      const macro =
        mode !== 'macro' ? '' : consoleMapped ? (defaults?.macro ?? '') : rawValue || defaults?.macro || `{${name}}`;
      const provenance =
        consoleMapped && defaults
          ? detectedOriginForMatch(defaults.macro, defaults.confidence, rawValue)
          : readValueOrigin(param.provenance);

      return createParameterRow({
        name,
        mode,
        value: mode === 'macro' ? '' : rawValue,
        macro,
        // Only a macro row has a default beneath it: on a static row the literal is the answer.
        default: mode === 'macro' && param.defaultValue !== undefined ? String(param.defaultValue) : undefined,
        optional: param.optional !== false,
        description: param.description ?? (mode === 'macro' ? 'Parsed macro parameter' : 'Parsed parameter'),
        mappingSource: param.mappingSource ?? defaults?.defaultMappingSource ?? '',
        // Carried, never invented (R4b). The parser decides what it detected; this
        // boundary only has to not drop it. A row that arrives marked `detected` and
        // is rendered unchipped reads as operator-set, which is the one wrong answer.
        // The one exception is the guess this boundary makes itself, above.
        provenance,
      });
    });
}

export function splitEndpointQuery(
  url: string,
  existing: ParameterRow[],
): { endpoint: string; added: ParameterRow[]; conflicts: string[]; warnings: string[] } | null {
  const trimmed = (url ?? '').trim();
  const qIndex = trimmed.indexOf('?');
  if (qIndex < 0) return null;
  const { suggestions, error } = parseSampleUrl(trimmed);
  if (error || suggestions.length === 0) return null;

  /**
   * `MappingSuggestion.sampleValue` is `unknown`, and a query string can only ever have
   * produced a string — but coercing through `String()` would render an object as
   * `[object Object]`, the placeholder the VAST replay path explicitly refuses to send. So a
   * non-primitive is treated as no value rather than as a value we invented.
   */
  const asText = (v: unknown): string =>
    typeof v === 'string' ? v : typeof v === 'number' || typeof v === 'boolean' ? String(v) : '';

  const byName = new Map(existing.map((r) => [r.name.trim().toLowerCase(), r]));
  const added: ParameterRow[] = [];
  const conflicts: string[] = [];
  /** A value shaped like a viewer's, kept as written — said, never mapped (`#1016`). */
  const warnings: string[] = [];
  for (const s of suggestions) {
    const name = s.key.split('.').pop() ?? s.key;
    const key = name.toLowerCase();
    const already = byName.get(key);
    if (!already && s.perViewerShape) warnings.push(s.reason);
    if (already) {
      const incoming = s.kind === 'macro' && s.macroToken ? s.macroToken : asText(s.sampleValue);
      if (incoming !== (already.value ?? '')) conflicts.push(name);
      continue;
    }
    // Marked, with the detector's grade and what Revert returns to (`#1001`): the parameters came out
    // of a pasted endpoint, and until then this path marked none of them.
    const row = createParameterRow(
      s.kind === 'macro' && s.macroToken
        ? { name, mode: 'macro', value: s.macroToken, provenance: detectedOriginFor(s) }
        : { name, mode: 'static', value: asText(s.sampleValue), provenance: detectedOriginFor(s) },
    );
    byName.set(key, row);
    added.push(row);
  }
  return { endpoint: trimmed.slice(0, qIndex), added, conflicts, warnings };
}

/**
 * **Whether two parameter row lists would store different parameters** (`#1484`), compared as they are
 * stored rather than as rows, so a row's client-side identity never reads as a change. The detail page's
 * Connection section writes `params` only when this is true, so a save that lifted nothing never writes
 * the parameters it did not touch.
 */
export function paramsDiffer(a: ParameterRow[], b: ParameterRow[]): boolean {
  return JSON.stringify(rowsToParamsDto(a).fields) !== JSON.stringify(rowsToParamsDto(b).fields);
}

/**
 * **Lift an endpoint's query string into Query Parameter rows, and say so** (#874 · D18, `#1484`).
 *
 * The act of authoring an endpoint, not a property of one field. It sat on the wizard's field alone, so
 * the same URL typed on the detail page stored whole and its parameters never became rows: *a rule
 * attached to one path lapses when another is added*. Every surface that edits an endpoint calls this,
 * so the split, the rows it adds and the sentence it says are one answer.
 *
 * The sentence is announced because a field that silently rewrites what someone pasted is the *"why'd
 * you change it?"* complaint this behaviour exists to answer. A differing value is reported, never
 * resolved for the operator: the row is the authored artifact and the pasted endpoint is an offer.
 * `null` where the endpoint carries no parameters to lift.
 */
export function liftEndpointQuery(
  endpoint: string,
  rows: ParameterRow[],
): { endpoint: string; parameterRows: ParameterRow[]; note: string } | null {
  const split = splitEndpointQuery(endpoint, rows);
  if (!split) return null;
  const moved =
    split.added.length > 0
      ? `Moved ${split.added.length} parameter${split.added.length === 1 ? '' : 's'} from the endpoint into Query Parameters — they are still sent, and now you can edit them.`
      : 'Moved the endpoint’s parameters into Query Parameters — they are still sent, and now you can edit them.';
  const clashed =
    split.conflicts.length > 0
      ? ` ${split.conflicts.length === 1 ? 'One parameter' : `${split.conflicts.length} parameters`} (${split.conflicts.join(', ')}) already had a row with a different value — the existing row is being sent. Check which you meant.`
      : '';
  const shaped = split.warnings.length > 0 ? ` ${split.warnings.join(' ')}` : '';
  return {
    endpoint: split.endpoint,
    parameterRows: [...rows, ...split.added],
    note: `${moved}${clashed}${shaped}`,
  };
}

/**
 * **Would closing the wizard lose work?** (#1129)
 *
 * Pulled out of the component for the reason `shouldReveal` is: it is the whole of the judgement,
 * the component cannot be render-tested here, and the case that was missing is invisible in the
 * happy path.
 *
 * **A different question from "are there unsaved changes".** That one asks *have you diverged from
 * what is stored* — the chip's question, and meaningless during a create, where everything is
 * unsaved until the operator presses Create and a permanent "Unsaved" would be noise. This asks
 * *would closing throw something away*, which is true in create mode the moment anything is typed.
 *
 * Conflating them is what left `Escape` discarding a part-built demand source with no confirm: the
 * guard existed, and it read `isEditMode && …`.
 */
export function wouldLoseWork(state: {
  isEditMode: boolean;
  /** The form as it stands, serialised. */
  current: string;
  /** The form as the wizard opened. */
  pristine: string;
  /** The form as last saved — `null` before the first save, and always in create mode. */
  lastSaved: string | null;
}): boolean {
  const { isEditMode, current, pristine, lastSaved } = state;
  // Edit mode compares against the store, so saving mid-flow genuinely clears the risk.
  if (isEditMode) return lastSaved !== null && current !== lastSaved;
  // Create mode has nothing stored to compare against; anything the operator typed is the work.
  return current !== pristine;
}

/**
 * Does this close attempt need to ask first?
 *
 * Two presses with **feedback between them** — a wizard that appears to ignore Escape is the failure
 * `useRevealOnOpen` documents, where the absence of a response reads as the absence of an effect and
 * the operator presses harder.
 */
export function shouldConfirmClose(losesWork: boolean, alreadyAsking: boolean): boolean {
  return losesWork && !alreadyAsking;
}
